import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { GiftDetailsIdMappedInput, GiftView } from 'src/app/auth_profile/model/v3/transactions';
import { ColNativeV3Api } from './col-native-v3-api';
import {
  ApiLessonEventModel,
  ProductAvailabilityRequest,
  ScheduleDefinition,
  ScheduleReference,
  SimpleLessonScheduleRequest,
  SimpleProductAvailability,
  SimpleTeacherProductAvailability,
  StudentColLessonScheduleEvents,
  TeacherColLessonScheduleEvents,
} from '../../../model/v3/booking';
import { AndroidBridgeBase } from '../../../../mobile/api-services/bridge/android-bridge-base';
import { LogsService } from '../../../../utils/services/logs.service';
import { Constraints, Platform } from '../../../../constraints';
import { Dates } from '../../../../utils/calendar-utils';
import { appendIfDefined, serializeDateInput } from '../../../../auth_profile/services/api/v3/rest-v3-utils';
import { Page, Pageable } from '../../../../utils/pageable';
import * as LessonsV3 from '../../../model/v3/lessons';
import {
  normalizeLessonBundlesPage,
  normalizeLessonBundle,
  normalizeLessonCommitsPage,
  normalizeLessonFlagDto,
  normalizeLessonFlagsDto,
  normalizeLessonMetricDto,
  normalizeLessonsPage,
  normalizeLessonView,
  normalizeProvaContext
} from '../../../utils/v3/lessons-v3-date-utils';

@Injectable({
  providedIn: 'root'
})
export class ColAndroidV3Service implements ColNativeV3Api {
  private readonly apiEndpoint = Constraints.androidGenericInternalApiEndpoint;

  constructor(
    private readonly logger: LogsService,
    private readonly http: HttpClient,
    private readonly bridgeBase: AndroidBridgeBase
  ) {}

  private static buildRequestIdHeader(id: number): HttpHeaders {
    return new HttpHeaders({ 'X-Request-Id': id.toString() });
  }

  private nextRequestIdHeader(): HttpHeaders {
    return ColAndroidV3Service.buildRequestIdHeader(this.bridgeBase.getNextRequestId());
  }

  private baseEventsUrl(path: string): string {
    return `${this.apiEndpoint}/platform/${Platform.Events}${path}`;
  }

  private studentUrl(schoolId: number, studentId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/schools/${schoolId}/students/${studentId}${path}`);
  }

  private teacherUrl(schoolId: number, teacherId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/schools/${schoolId}/teachers/${teacherId}${path}`);
  }

  private lessonsUrl(path: string = ''): string {
    return this.baseEventsUrl(`/v1/lessons${path}`);
  }

  private peopleColSettingsUrl(personId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/people/${personId}/col-settings${path}`);
  }

  private studentBundlesUrl(schoolId: number, studentId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/schools/${schoolId}/students/${studentId}/bundles${path}`);
  }

  private studentCommitsUrl(schoolId: number, studentId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/schools/${schoolId}/students/${studentId}/commits${path}`);
  }

  private schoolProductsUrl(schoolId: number, path: string = ''): string {
    return this.baseEventsUrl(`/v1/schools/${schoolId}/products${path}`);
  }

  private productContextsUrl(
    schoolId: number,
    studentId: number,
    productCode?: string,
    path: string = ''
  ): string {
    const productCodePath = productCode ? `/${productCode}` : '';
    return this.baseEventsUrl(`/v1/schools/${schoolId}/students/${studentId}/product-contexts${productCodePath}${path}`);
  }

  private appendDateRange(params: HttpParams, dateFrom?: Date | null, dateTo?: Date | null): HttpParams {
    let next = appendIfDefined(params, 'dateFrom', serializeDateInput(dateFrom));
    next = appendIfDefined(next, 'dateTo', serializeDateInput(dateTo));
    return next;
  }

  private appendPageable(params: HttpParams, pageable?: Pageable | null): HttpParams {
    if (!pageable) {
      return params;
    }
    return pageable.appendParams(params);
  }

  private appendAll(params: HttpParams, key: string, values?: string[] | null): HttpParams {
    let next = params;
    (values ?? []).forEach((value) => {
      next = next.append(key, value);
    });
    return next;
  }

  private parseEventDates<T extends { events: { eventDate: any }[] }>(items: T[]): T[] {
    return items.map((item) => {
      item.events.forEach((ev) => {
        ev.eventDate = ev.eventDate instanceof Date
          ? ev.eventDate
          : Dates.parse(String(ev.eventDate));
      });
      return item;
    });
  }

  private parseScheduleEvents<T extends { events: { eventDate: any }[] }>(schedule: T): T {
    schedule.events.forEach((ev) => {
      ev.eventDate = ev.eventDate instanceof Date
        ? ev.eventDate
        : Dates.parse(String(ev.eventDate));
    });
    return schedule;
  }

  getTeacherAvailability(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleTeacherProductAvailability[]> {
    this.logger.log(`(v3) getting teacher availability for schoolId=${schoolId}, studentId=${studentId}`);
    let params = this.appendDateRange(new HttpParams(), dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<SimpleTeacherProductAvailability[]>(
      this.studentUrl(schoolId, studentId, '/teacher-availability'),
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getStudentLessonSchedules(
    schoolId: number,
    studentId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<StudentColLessonScheduleEvents[]> {
    this.logger.log(`(v3) getting student lesson schedules for schoolId=${schoolId}, studentId=${studentId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<StudentColLessonScheduleEvents[]>(
      this.studentUrl(schoolId, studentId, '/schedules'),
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  reserveColLessonSchedule(
    schoolId: number,
    studentId: number,
    request: SimpleLessonScheduleRequest
  ): Observable<StudentColLessonScheduleEvents> {
    this.logger.log(`(v3) reserving col lesson schedule for schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.post<StudentColLessonScheduleEvents>(
      this.studentUrl(schoolId, studentId, '/schedules'),
      request,
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  getStudentSchedule(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<StudentColLessonScheduleEvents> {
    this.logger.log(`(v3) getting student schedule for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'lesson');
    return this.http.get<StudentColLessonScheduleEvents>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`),
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedule) => this.parseScheduleEvents(schedule)));
  }

  updateColLessonEventData(
    schoolId: number,
    studentId: number,
    scheduleId: number,
    eventId: number,
    data: ApiLessonEventModel
  ): Observable<void> {
    this.logger.log(`(v3) updating col lesson event data for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.put<void>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}/events/${eventId}/data`),
      data,
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  deleteColLesson(
    schoolId: number,
    studentId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting col lesson for schoolId=${schoolId}, studentId=${studentId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.delete<void>(
      this.studentUrl(schoolId, studentId, `/schedules/${scheduleId}`),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  getAvailabilityEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    productCode?: string | null
  ): Observable<SimpleProductAvailability[]> {
    this.logger.log(`(v3) getting availability events for schoolId=${schoolId}, teacherId=${teacherId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<SimpleProductAvailability[]>(
      this.teacherUrl(schoolId, teacherId, '/schedule-events'),
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherColLessonEvents(
    schoolId: number,
    teacherId: number,
    dateFrom?: Date | null,
    dateTo?: Date | null
  ): Observable<TeacherColLessonScheduleEvents[]> {
    this.logger.log(`(v3) getting teacher col lesson events for schoolId=${schoolId}, teacherId=${teacherId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    params = this.appendDateRange(params, dateFrom, dateTo);
    return this.http.get<TeacherColLessonScheduleEvents[]>(
      this.teacherUrl(schoolId, teacherId, '/schedule-events'),
      { headers: this.nextRequestIdHeader(), params }
    ).pipe(map((schedules) => this.parseEventDates(schedules)));
  }

  getTeacherCourseProfiles(
    schoolId: number,
    teacherId: number
  ): Observable<LessonsV3.V3CourseTeacherProfile[]> {
    this.logger.log(`(v3) getting teacher course profiles for schoolId=${schoolId}, teacherId=${teacherId}`);
    return this.http.get<LessonsV3.V3CourseTeacherProfile[]>(
      this.teacherUrl(schoolId, teacherId, '/course-profiles'),
      { headers: this.nextRequestIdHeader() }
    );
  }

  createAvailability(
    schoolId: number,
    teacherId: number,
    request: ProductAvailabilityRequest
  ): Observable<ScheduleReference> {
    this.logger.log(`(v3) creating availability for schoolId=${schoolId}, teacherId=${teacherId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    return this.http.post<ScheduleReference>(
      this.teacherUrl(schoolId, teacherId, '/schedules'),
      request,
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  deleteAvailability(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting availability for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'availability');
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  deleteTeacherColLesson(
    schoolId: number,
    teacherId: number,
    scheduleId: number
  ): Observable<void> {
    this.logger.log(`(v3) deleting teacher col lesson for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}`);
    const params = appendIfDefined(new HttpParams(), 'type', 'col_lesson');
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}`),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  deleteAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    deleteAll?: boolean | null
  ): Observable<void> {
    this.logger.log(`(v3) deleting availability event for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'deleteAll', deleteAll);
    return this.http.delete<void>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  updateAvailabilityEvent(
    schoolId: number,
    teacherId: number,
    scheduleId: number,
    eventId: number,
    definition: ScheduleDefinition,
    applyFuture?: boolean | null
  ): Observable<ScheduleDefinition> {
    this.logger.log(`(v3) updating availability event for schoolId=${schoolId}, teacherId=${teacherId}, scheduleId=${scheduleId}, eventId=${eventId}`);
    let params = appendIfDefined(new HttpParams(), 'type', 'availability');
    params = appendIfDefined(params, 'applyFuture', applyFuture);
    return this.http.put<ScheduleDefinition>(
      this.teacherUrl(schoolId, teacherId, `/schedules/${scheduleId}/events/${eventId}`),
      definition,
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  getStudentGifts(
    schoolId: number,
    studentId: number,
    unused?: boolean | null,
    platform?: string | null
  ): Observable<GiftView[]> {
    this.logger.log(`(v3) getting student gifts for schoolId=${schoolId}, studentId=${studentId}`);
    let params = appendIfDefined(new HttpParams(), 'unused', unused);
    params = appendIfDefined(params, 'platform', platform);
    return this.http.get<GiftView[]>(
      this.studentUrl(schoolId, studentId, '/gifts'),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  createStudentGift(
    schoolId: number,
    studentId: number,
    details: GiftDetailsIdMappedInput,
    _platform?: string | null
  ): Observable<GiftDetailsIdMappedInput> {
    this.logger.log(`(v3) creating student gift for schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'platform', 'EVENTS');
    return this.http.post<GiftDetailsIdMappedInput>(
      this.studentUrl(schoolId, studentId, '/gifts'),
      details,
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  findLessons(
    type?: LessonsV3.LessonCategory | null,
    lessonType?: LessonsV3.LessonType | null,
    view?: string | null,
    eid?: string | null,
    studentId?: number | null,
    teacherId?: number | null,
    groupId?: number | null,
    groupEid?: string | null,
    productCode?: string | null,
    statuses?: LessonsV3.LessonStatus[] | null,
    dateFrom?: Date | null,
    dateTo?: Date | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.LessonView>> {
    this.logger.log('(v3) finding lessons');
    const normalizedProductCode = productCode?.trim() ? productCode.trim() : null;
    let params = new HttpParams();
    params = appendIfDefined(params, 'type', type);
    params = appendIfDefined(params, 'lessonType', lessonType);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'eid', eid);
    params = appendIfDefined(params, 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    params = appendIfDefined(params, 'groupId', groupId);
    params = appendIfDefined(params, 'groupEid', groupEid);
    params = appendIfDefined(params, 'productCode', normalizedProductCode);
    params = this.appendAll(params, 'statuses', statuses ?? null);
    params = this.appendDateRange(params, dateFrom, dateTo);
    params = this.appendPageable(params, pageable);
    return this.http
      .get<Page<LessonsV3.LessonView>>(this.lessonsUrl(), { headers: this.nextRequestIdHeader(), params })
      .pipe(map((page) => normalizeLessonsPage(page)));
  }

  getLesson(
    lessonId: number,
    view?: string | null,
    viewStudentId?: number | null
  ): Observable<LessonsV3.LessonView> {
    this.logger.log(`(v3) getting lesson lessonId=${lessonId}`);
    let params = appendIfDefined(new HttpParams(), 'view', view);
    params = appendIfDefined(params, 'viewStudentId', viewStudentId);
    return this.http
      .get<LessonsV3.LessonView>(this.lessonsUrl(`/${lessonId}`), { headers: this.nextRequestIdHeader(), params })
      .pipe(map((lesson) => normalizeLessonView(lesson)));
  }

  getLessonMetric(lessonId: number): Observable<LessonsV3.V3LessonMetric> {
    this.logger.log(`(v3) getting lesson metric lessonId=${lessonId}`);
    return this.http
      .get<LessonsV3.V3LessonMetric>(this.lessonsUrl(`/${lessonId}/metric`), { headers: this.nextRequestIdHeader() })
      .pipe(map((metric) => normalizeLessonMetricDto(metric)));
  }

  sendLessonNotification(lessonId: number): Observable<void> {
    this.logger.log(`(v3) sending lesson notification lessonId=${lessonId}`);
    return this.http.post<void>(this.lessonsUrl(`/${lessonId}/notifications`), null, { headers: this.nextRequestIdHeader() });
  }

  getLessonComment(
    lessonId: number,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    this.logger.log(`(v3) getting lesson comment lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    return this.http.get<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), { headers: this.nextRequestIdHeader(), params });
  }

  updateLessonComments(
    lessonId: number,
    message: LessonsV3.LessonMessageDto,
    studentId?: number | null
  ): Observable<LessonsV3.ApiLessonMessage> {
    this.logger.log(`(v3) updating lesson comments lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    return this.http.put<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), message, { headers: this.nextRequestIdHeader(), params });
  }

  getLessonFlags(lessonId: number): Observable<LessonsV3.ApiLessonFlag[]> {
    this.logger.log(`(v3) getting lesson flags lessonId=${lessonId}`);
    return this.http
      .get<LessonsV3.ApiLessonFlag[]>(this.lessonsUrl(`/${lessonId}/flags`), { headers: this.nextRequestIdHeader() })
      .pipe(map((flags) => normalizeLessonFlagsDto(flags)));
  }

  createLessonFlag(
    lessonId: number,
    details: LessonsV3.LessonFlagDto,
    studentId?: number | null,
    teacherId?: number | null
  ): Observable<LessonsV3.ApiLessonFlag> {
    this.logger.log(`(v3) creating lesson flag lessonId=${lessonId}`);
    let params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    return this.http
      .post<LessonsV3.ApiLessonFlag>(this.lessonsUrl(`/${lessonId}/flags`), details, { headers: this.nextRequestIdHeader(), params })
      .pipe(map((flag) => normalizeLessonFlagDto(flag)));
  }

  deleteLessonFlag(lessonId: number, flagId: number): Observable<void> {
    this.logger.log(`(v3) deleting lesson flag lessonId=${lessonId}, flagId=${flagId}`);
    return this.http.delete<void>(this.lessonsUrl(`/${lessonId}/flags/${flagId}`), { headers: this.nextRequestIdHeader() });
  }

  getLessonGroup(lessonId: number): Observable<LessonsV3.LessonGroupParticipant> {
    this.logger.log(`(v3) getting lesson group lessonId=${lessonId}`);
    return this.http.get<LessonsV3.LessonGroupParticipant>(this.lessonsUrl(`/${lessonId}/group`), { headers: this.nextRequestIdHeader() });
  }

  getLessonTeacher(
    lessonId: number
  ): Observable<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>> {
    this.logger.log(`(v3) getting lesson teacher lessonId=${lessonId}`);
    return this.http.get<LessonsV3.LessonTeacherParticipant<LessonsV3.TeacherRef>>(this.lessonsUrl(`/${lessonId}/teacher`), {
      headers: this.nextRequestIdHeader()
    });
  }

  getLessonStudents(
    lessonId: number
  ): Observable<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]> {
    this.logger.log(`(v3) getting lesson students lessonId=${lessonId}`);
    return this.http.get<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]>(this.lessonsUrl(`/${lessonId}/students`), {
      headers: this.nextRequestIdHeader()
    });
  }

  getLessonStudentProgress(
    lessonId: number,
    studentNumber: number
  ): Observable<LessonsV3.V3LessonParticipantProgressDetails> {
    this.logger.log(`(v3) getting lesson student progress lessonId=${lessonId}, studentNumber=${studentNumber}`);
    return this.http.get<LessonsV3.V3LessonParticipantProgressDetails>(
      this.lessonsUrl(`/${lessonId}/students/${studentNumber}/progress`),
      { headers: this.nextRequestIdHeader() }
    );
  }

  getLessonNotes(lessonId: number): Observable<Record<string, string>> {
    this.logger.log(`(v3) getting lesson notes lessonId=${lessonId}`);
    return this.http.get<Record<string, string>>(this.lessonsUrl(`/${lessonId}/notes`), { headers: this.nextRequestIdHeader() });
  }

  getLessonProgress(lessonId: number): Observable<LessonsV3.LessonProgressDetails> {
    this.logger.log(`(v3) getting lesson progress lessonId=${lessonId}`);
    return this.http.get<LessonsV3.LessonProgressDetails>(this.lessonsUrl(`/${lessonId}/progress`), { headers: this.nextRequestIdHeader() });
  }

  updateLessonProgress(
    lessonId: number,
    progress: LessonsV3.LessonProgressDetailsDto,
    type?: LessonsV3.LessonProgressType | null
  ): Observable<LessonsV3.LessonProgressDetails> {
    this.logger.log(`(v3) updating lesson progress lessonId=${lessonId}`);
    const params = appendIfDefined(new HttpParams(), 'type', type);
    return this.http.put<LessonsV3.LessonProgressDetails>(this.lessonsUrl(`/${lessonId}/progress`), progress, {
      headers: this.nextRequestIdHeader(),
      params
    });
  }

  updateLessonType(
    lessonId: number,
    type: LessonsV3.LessonType
  ): Observable<void> {
    this.logger.log(`(v3) updating lesson type lessonId=${lessonId}`);
    return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/type`), type, { headers: this.nextRequestIdHeader() });
  }

  updateLessonStatus(
    lessonId: number,
    actionOrStatus: LessonsV3.LessonStatusAction | LessonsV3.LessonStatus,
    request?: LessonsV3.LessonStatusRequestDto | null,
    studentId?: number | null,
    teacherId?: number | null,
    checkStatus?: boolean | null
  ): Observable<LessonsV3.LessonView | void> {
    this.logger.log(`(v3) updating lesson status lessonId=${lessonId} actionOrStatus=${actionOrStatus}`);
    const isAction = Object.values(LessonsV3.LessonStatusAction).includes(actionOrStatus as LessonsV3.LessonStatusAction);
    if (isAction) {
      let params = appendIfDefined(new HttpParams(), 'action', actionOrStatus);
      params = appendIfDefined(params, 'studentId', studentId);
      params = appendIfDefined(params, 'teacherId', teacherId);
      params = appendIfDefined(params, 'checkStatus', checkStatus);
      return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/status`), request, {
        headers: this.nextRequestIdHeader(),
        params
      });
    }
    return this.http.patch<void>(this.lessonsUrl(`/${lessonId}/status`), actionOrStatus, { headers: this.nextRequestIdHeader() });
  }

  getPersonColSettings(personId: number): Observable<LessonsV3.PersonalSettings> {
    this.logger.log(`(v3) getting person col settings personId=${personId}`);
    return this.http.get<LessonsV3.PersonalSettings>(this.peopleColSettingsUrl(personId), { headers: this.nextRequestIdHeader() });
  }

  updatePersonColSettings(
    personId: number,
    settings: LessonsV3.PersonalSettings
  ): Observable<LessonsV3.PersonalSettings> {
    this.logger.log(`(v3) updating person col settings personId=${personId}`);
    return this.http.put<LessonsV3.PersonalSettings>(this.peopleColSettingsUrl(personId), settings, { headers: this.nextRequestIdHeader() });
  }

  updatePersonIntroductionState(
    personId: number,
    state: LessonsV3.IntroductionState
  ): Observable<void> {
    this.logger.log(`(v3) updating person introduction state personId=${personId}`);
    return this.http.patch<void>(this.peopleColSettingsUrl(personId, '/introduction-state'), state, { headers: this.nextRequestIdHeader() });
  }

  getStudentBundles(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    available?: boolean | null
  ): Observable<Page<LessonsV3.LessonBundle>> {
    this.logger.log(`(v3) getting student bundles schoolId=${schoolId}, studentId=${studentId}`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'available', available);
    return this.http.get<Page<LessonsV3.LessonBundle>>(this.studentBundlesUrl(schoolId, studentId), {
      headers: this.nextRequestIdHeader(),
      params
    }).pipe(map((page) => normalizeLessonBundlesPage(page)));
  }

  createStudentBundle(
    schoolId: number,
    studentId: number,
    details: LessonsV3.LessonBundleDetails
  ): Observable<LessonsV3.LessonBundle> {
    this.logger.log(`(v3) creating student bundle schoolId=${schoolId}, studentId=${studentId}`);
    return this.http
      .post<LessonsV3.LessonBundle>(this.studentBundlesUrl(schoolId, studentId), details, { headers: this.nextRequestIdHeader() })
      .pipe(map((bundle) => normalizeLessonBundle(bundle)));
  }

  updateStudentBundleExpiry(
    schoolId: number,
    studentId: number,
    bundleId: number,
    expiryDate: Date
  ): Observable<void> {
    this.logger.log(`(v3) updating student bundle expiry schoolId=${schoolId}, studentId=${studentId}, bundleId=${bundleId}`);
    return this.http.patch<void>(this.studentBundlesUrl(schoolId, studentId, `/${bundleId}/expiry-date`), expiryDate, {
      headers: this.nextRequestIdHeader()
    });
  }

  getStudentCommits(
    schoolId: number,
    studentId: number,
    pageable?: Pageable | null,
    productCode?: string | null,
    courseCode?: string | null,
    commitStatuses?: LessonsV3.LessonCommitStatus[] | null,
    lessonStatuses?: LessonsV3.LessonStatus[] | null
  ): Observable<Page<LessonsV3.LessonCommit>> {
    this.logger.log(`(v3) getting student commits schoolId=${schoolId}, studentId=${studentId}`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'productCode', productCode);
    params = appendIfDefined(params, 'courseCode', courseCode);
    params = this.appendAll(params, 'commitStatuses', commitStatuses ?? null);
    params = this.appendAll(params, 'lessonStatuses', lessonStatuses ?? null);
    return this.http.get<Page<LessonsV3.LessonCommit>>(this.studentCommitsUrl(schoolId, studentId), {
      headers: this.nextRequestIdHeader(),
      params
    }).pipe(map((page) => normalizeLessonCommitsPage(page)));
  }

  getStudents(
    schoolId?: number | null,
    personId?: number | null,
    email?: string | null,
    studentIds?: number[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.V3SchoolStudent>> {
    this.logger.log(`(v3) getting casa students perspective`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'schoolId', schoolId);
    params = appendIfDefined(params, 'personId', personId);
    params = appendIfDefined(params, 'email', email);
    params = this.appendAll(params, 'studentIds', (studentIds ?? []).map((id) => `${id}`));
    return this.http.get<Page<LessonsV3.V3SchoolStudent>>(
      this.baseEventsUrl('/v1/students'),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  getSchoolProducts(schoolId: number): Observable<LessonsV3.V3Product[]> {
    this.logger.log(`(v3) getting school products schoolId=${schoolId}`);
    return this.http.get<LessonsV3.V3Product[]>(this.schoolProductsUrl(schoolId), { headers: this.nextRequestIdHeader() });
  }

  getProductVariants(
    view?: string | null,
    platform?: string | null,
    variantCodes?: string[] | null,
    lang?: string | null,
    currency?: string | null,
    priceLists?: string[] | null,
    pageable?: Pageable | null
  ): Observable<Page<LessonsV3.ProductVariantPreview>> {
    this.logger.log(`(v3) getting product variants`);
    let params = new HttpParams();
    params = this.appendPageable(params, pageable);
    params = appendIfDefined(params, 'view', view);
    params = appendIfDefined(params, 'platform', platform);
    params = this.appendAll(params, 'variantCode', variantCodes ?? null);
    params = appendIfDefined(params, 'lang', lang);
    params = appendIfDefined(params, 'currency', currency);
    params = this.appendAll(params, 'priceList', priceLists ?? null);
    return this.http.get<Page<LessonsV3.ProductVariantPreview>>(
      this.baseEventsUrl('/v1/product-variants'),
      { headers: this.nextRequestIdHeader(), params }
    );
  }

  getProductCourses(
    schoolId: number,
    productCode: string
  ): Observable<LessonsV3.Course[]> {
    this.logger.log(`(v3) getting product courses schoolId=${schoolId}, productCode=${productCode}`);
    return this.http.get<LessonsV3.Course[]>(
      this.schoolProductsUrl(schoolId, `/${productCode}/courses`),
      { headers: this.nextRequestIdHeader() }
    );
  }

  getCourseCompetences(
    schoolId: number,
    productCode: string,
    courseCode: string
  ): Observable<LessonsV3.Competence[]> {
    this.logger.log(`(v3) getting course competences schoolId=${schoolId}, productCode=${productCode}, courseCode=${courseCode}`);
    return this.http.get<LessonsV3.Competence[]>(
      this.schoolProductsUrl(schoolId, `/${productCode}/courses/${courseCode}/competences`),
      { headers: this.nextRequestIdHeader() }
    );
  }

  getCompetenceMappings(): Observable<LessonsV3.CompetenceMapping[]> {
    this.logger.log(`(v3) getting competence mappings`);
    return this.http.get<LessonsV3.CompetenceMapping[]>(
      this.baseEventsUrl('/v1/products/competence-mappings'),
      { headers: this.nextRequestIdHeader() }
    );
  }

  getProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string
  ): Observable<LessonsV3.ColProvaContextDetails> {
    this.logger.log(`(v3) getting prova context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    return this.http.get<LessonsV3.ColProvaContextDetails>(
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context'),
      { headers: this.nextRequestIdHeader() }
    ).pipe(map((context) => normalizeProvaContext(context)));
  }

  updateProvaContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ColProvaContextDetails
  ): Observable<LessonsV3.ColProvaContextDetails> {
    this.logger.log(`(v3) updating prova context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    return this.http.put<LessonsV3.ColProvaContextDetails>(
      this.productContextsUrl(schoolId, studentId, productCode, '/prova-context'),
      context,
      { headers: this.nextRequestIdHeader() }
    ).pipe(map((updated) => normalizeProvaContext(updated)));
  }

  getProductContexts(
    schoolId: number,
    studentId: number,
    productCode?: string | null
  ): Observable<LessonsV3.ProductContextDetails[]> {
    this.logger.log(`(v3) getting product contexts schoolId=${schoolId}, studentId=${studentId}`);
    const params = appendIfDefined(new HttpParams(), 'productCode', productCode);
    return this.http.get<LessonsV3.ProductContextDetails[]>(this.productContextsUrl(schoolId, studentId), {
      headers: this.nextRequestIdHeader(),
      params
    });
  }

  updateProductContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ProductContextDetails
  ): Observable<void> {
    this.logger.log(`(v3) updating product context schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    return this.http.put<void>(this.productContextsUrl(schoolId, studentId, productCode), context, {
      headers: this.nextRequestIdHeader()
    });
  }

  getCourseContexts(
    schoolId: number,
    studentId: number,
    productCode: string,
    current?: boolean | null
  ): Observable<LessonsV3.ColCourseContext[]> {
    this.logger.log(`(v3) getting course contexts schoolId=${schoolId}, studentId=${studentId}, productCode=${productCode}`);
    const params = appendIfDefined(new HttpParams(), 'current', current);
    return this.http.get<LessonsV3.ColCourseContext[]>(
      this.productContextsUrl(schoolId, studentId, productCode, '/course-contexts'),
      { headers: this.nextRequestIdHeader(), params }
    );
  }
}
